// Google Analytics event tracking utility
const getEnvironment = () => process.env.NEXT_PUBLIC_APP_ENV || 'development';

// Type definitions for GA events
export type EventCategory =
  | 'interaction'
  | 'feedback'
  | 'user'
  | 'admin'
  | 'content'
  | 'performance'
  | 'navigation'
  | 'question'
  | 'conversation';

export type EventAction =
  | 'suggested_question_click'
  | 'legal_source_click'
  | 'ccq_article_click'
  | 'feedback_submit'
  | 'feedback_positive'
  | 'feedback_negative'
  | 'user_login'
  | 'user_signup'
  | 'user_logout'
  | 'question_asked'
  | 'message_response'
  | 'admin_page_view'
  | 'admin_metric_filter'
  | 'profile_view'
  | 'profile_edit'
  | 'profile_update'
  | 'button_click'
  | 'link_click'
  | 'form_submit'
  | 'feedback_given'
  | 'page_view'
  | 'conversation_started'
  | 'conversation_ended';

// Analytics integration for page views and events
export interface AnalyticsEvent {
  category: EventCategory;
  action: EventAction;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
}

/**
 * Track a custom event in Google Analytics
 */
export function trackEvent({
  category,
  action,
  label = '',
  value = 0,
  nonInteraction = false,
}: AnalyticsEvent) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    try {
      (window as any).gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        non_interaction: nonInteraction,
      });
    } catch (error) {
      // Silently fail in production
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to track event', error);
      }
    }
  }
}

/**
 * Legacy trackEvent method for backward compatibility
 * @deprecated Use the object parameter version instead
 */
export function trackEventLegacy(
  category: EventCategory,
  action: EventAction,
  label?: string,
  value?: number,
  nonInteraction?: boolean
) {
  trackEvent({
    category,
    action,
    label,
    value,
    nonInteraction,
  });
}

// Predefined event categories
export const EventCategory = {
  INTERACTION: 'interaction' as EventCategory,
  FEEDBACK: 'feedback' as EventCategory,
  USER: 'user' as EventCategory,
  ADMIN: 'admin' as EventCategory,
  CONTENT: 'content' as EventCategory,
  PERFORMANCE: 'performance' as EventCategory,
  NAVIGATION: 'navigation' as EventCategory,
  QUESTION: 'question' as EventCategory,
  CONVERSATION: 'conversation' as EventCategory,
} as const;

// Predefined event actions
export const EventAction = {
  // Interaction events
  SUGGESTED_QUESTION_CLICK: 'suggested_question_click' as EventAction,
  LEGAL_SOURCE_CLICK: 'legal_source_click' as EventAction,
  CCQ_ARTICLE_CLICK: 'ccq_article_click' as EventAction,
  QUESTION_ASKED: 'question_asked' as EventAction,
  BUTTON_CLICK: 'button_click' as EventAction,
  LINK_CLICK: 'link_click' as EventAction,
  FORM_SUBMIT: 'form_submit' as EventAction,

  // Feedback events
  FEEDBACK_SUBMIT: 'feedback_submit' as EventAction,
  FEEDBACK_POSITIVE: 'feedback_positive' as EventAction,
  FEEDBACK_NEGATIVE: 'feedback_negative' as EventAction,
  FEEDBACK_GIVEN: 'feedback_given' as EventAction,

  // User events
  USER_LOGIN: 'user_login' as EventAction,
  USER_SIGNUP: 'user_signup' as EventAction,
  USER_LOGOUT: 'user_logout' as EventAction,
  PROFILE_VIEW: 'profile_view' as EventAction,
  PROFILE_EDIT: 'profile_edit' as EventAction,
  PROFILE_UPDATE: 'profile_update' as EventAction,

  // Performance events
  MESSAGE_RESPONSE: 'message_response' as EventAction,

  // Admin events
  ADMIN_PAGE_VIEW: 'admin_page_view' as EventAction,
  ADMIN_METRIC_FILTER: 'admin_metric_filter' as EventAction,

  // Navigation events
  PAGE_VIEW: 'page_view' as EventAction,

  // Conversation events
  CONVERSATION_STARTED: 'conversation_started' as EventAction,
  CONVERSATION_ENDED: 'conversation_ended' as EventAction,
} as const;

// Page view tracking
export const trackPageView = (path: string, title: string) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      environment: getEnvironment(),
      app_section: 'app', // Custom dimension
    });

    if (isDebugMode()) {
      console.debug(`[GA PageView]`, { path, title });
    }
  }
};

// Debug mode helper
export const isDebugMode = () => {
  const env = getEnvironment();
  return env !== 'production';
};

// Initialize GA4 custom events for our application
export const initializeGACustomEvents = () => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    // Configure custom metrics
    (window as any).gtag('config', getMeasurementId(), {
      custom_map: {
        dimension1: 'environment',
        dimension2: 'user_type',
        dimension3: 'topic',
        metric1: 'response_time',
        metric2: 'message_count',
        metric3: 'feedback_rate',
      },
    });
  }
};

// Get measurement ID based on environment
export const getMeasurementId = () => {
  const env = process.env.NEXT_PUBLIC_APP_ENV || 'development';
  switch (env) {
    case 'production':
      return 'G-1GH792P5JN'; //valid one, old: 'G-DWPQ1LW243'
    case 'demo':
      return 'G-1GH792P5JN';
    case 'development':
    default:
      return 'G-G6MNPQGXZJ';
  }
};
